'use client';
import { useToastContext } from '#pplx/frontend/aether/toasts/context/useToastContext.js';
import { ENTROPY_SOURCE } from '#pplx/frontend/comet/client/utils.js';
import { isImage, isSvg, resizeImageToMaxDimension } from '#pplx/frontend/common/files/images.js';
import { cleanCloudinaryUrl, getMimeTypeFromUrl, isCloudinaryURL, } from '#pplx/frontend/common/files/urls.js';
import { safeRandomUUID } from '#pplx/frontend/common/safeRandomUUID.js';
import { batchCreateUploadUrls, subscribeAttachments, subscribeAttachmentsBatch, } from '#pplx/frontend/components/hooks/attachments.js';
import { uploadConnectorFile } from '#pplx/frontend/components/hooks/connectorAttachmentUpload.js';
import { fileExtension, fileUploadSourceType, useTrackedFileUploadAnalytics, } from '#pplx/frontend/components/hooks/fileUploadAnalytics.js';
import { createImageFileFromStorage } from '#pplx/frontend/components/hooks/mediaFileUrlIngress.js';
import { boxItemToPickerDocument, dropboxItemToPickerDocument, } from '#pplx/frontend/components/hooks/toPickerDocument.js';
import { useBoxPicker } from '#pplx/frontend/components/hooks/useBoxPicker.js';
import { useDropboxPicker } from '#pplx/frontend/components/hooks/useDropboxPicker.js';
import { useGoogleDrivePicker } from '#pplx/frontend/components/hooks/useGoogleDrivePicker.js';
import { ModalOrigin } from '#pplx/frontend/components/types/types.js';
import { ATTACHMENT_FAILURE_MESSAGES, AttachmentProcessingErrorCodes, AttachmentUploadErrorCodes, isIgnorableAttachmentError, } from '#pplx/frontend/components/utils/attachmentsUtils.js';
import { BLOCKED_UPLOAD_MIME_TYPES, BYTES_PER_MB, ERROR_MESSAGE_TIMEOUT, FALLBACK_MAX_FILE_UPLOAD_SIZE_MB, MIN_FILE_UPLOAD_SIZE, } from '#pplx/frontend/components/utils/consts.js';
import { isFileImage } from '#pplx/frontend/components/utils/fileUploadUtils.js';
import { logger } from '#pplx/frontend/logger/browser.js';
import { useQuery } from '#pplx/frontend/react-query/useQuery.js';
import { useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
/**
 * Upload error codes a backend may return as `results[id].error`. Every message
 * here must be value-free: this path has nothing to substitute placeholders with.
 */
const SELF_CONTAINED_UPLOAD_ERRORS = [
    AttachmentUploadErrorCodes.attachments_disabled_by_organization,
    AttachmentUploadErrorCodes.over_batch_size,
    AttachmentUploadErrorCodes.over_file_size,
];
function isListOfFiles(files) {
    return files.every((file) => file instanceof File);
}
function preserveRequestHeaders(headers) {
    return headers;
}
export const useFileUpload = ({ fileInputRef, isSignedIn, uploadRateLimit, maxFilesUpload, attachmentTokenEstimationParams, forwardedRef, onFilePickerOpen, onStart, onComplete, openLoginModal, openPricingModal, gateUploadFile, openVisitorLoginUpsell, openMicrosoftPicker: openMicrosoftPickerInternal, closeMicrosoftPicker, errorMicrosoftPicker, toPickerDocument, setUploadRateLimit, 
// When translations are not provided, use default English strings and fallback to the key
isFollowUp, forceImage, refreshBoxCredentials, setUploadsReady, getImageUrl, isAudioVideoFilesEnabled = false, reason, maxAttachmentFileSizeMB = FALLBACK_MAX_FILE_UPLOAD_SIZE_MB, specialCapabilities = {
    unlimitedProSearch: false,
    maxModelSelection: false,
    unlimitedResearch: false,
    fileUpload: false,
}, onRemoveFile, getUserSettingsQueryKey, getUserSettings: getUserSettingsFunc, skipParsing = false, defaultFileSource, buildRequestHeaders = preserveRequestHeaders, googlePromptBehavior, analyticsSurface, }) => {
    const { $t, locale } = useIntl();
    const { started, completed } = useTrackedFileUploadAnalytics(analyticsSurface);
    const { openToast } = useToastContext();
    const [errorMessage, setErrorMessage] = useState(null);
    const [warningMessage, setWarningMessage] = useState(null);
    // Tracks whether any attachment in the current session has hit the backend
    // token-limit warning, so we can suppress additional generic warnings.
    const [hasTokenLimitExceeded, setHasTokenLimitExceeded] = useState(false);
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const startAttempt = useCallback((id, file, sourceType, fileCount) => {
        const size = file instanceof File ? file.size : (file?.sizeBytes ?? null);
        const attempt = {
            uploadAttemptUUID: id,
            fileUUID: null,
            sourceType: fileUploadSourceType(sourceType),
            fileSizeBytes: size,
            fileExtension: fileExtension(file?.name ?? ''),
            fileCount,
        };
        started(attempt);
        return attempt;
    }, [started]);
    const startAttempts = useCallback((files, source, selectionFileCount) => {
        const attempts = new Map();
        files.forEach((file) => {
            attempts.set(file, startAttempt(safeRandomUUID(), file, source, selectionFileCount));
        });
        return attempts;
    }, [startAttempt]);
    const rejectTooManyFiles = useCallback((attempts) => {
        setErrorMessage($t(ATTACHMENT_FAILURE_MESSAGES.over_file_count, {
            maxNumFiles: maxFilesUpload,
        }));
        attempts.forEach((attempt) => completed(attempt, 'failure', 'too_many_files'));
    }, [$t, completed, maxFilesUpload]);
    const removeFile = useCallback((id) => {
        setUploadedFiles((files) => files.filter((file) => file.id !== id));
    }, []);
    const [status, setStatus] = useState(uploadedFiles && uploadedFiles.length > 0 ? 'success' : 'idle');
    // TODO (jdong): remove one source constraint
    const [uploadSource, setUploadSource] = useState(null);
    const readableMaxAttachmentFileSize = `${maxAttachmentFileSizeMB}MB`;
    useEffect(() => {
        if (!setUploadsReady)
            return;
        if (!uploadedFiles.length) {
            setUploadsReady(true);
            return;
        }
        const isReady = (file) => file.status === 'parsing' || file.status === 'success' || file.status === 'failed';
        const filesReady = uploadedFiles.every(isReady);
        setUploadsReady(filesReady);
    }, [uploadedFiles, setUploadsReady]);
    useImperativeHandle(forwardedRef, () => ({
        uploadFiles: (files, selectionFileCount) => {
            const selectedFiles = Array.from(files);
            handleFileInput(selectedFiles, undefined, undefined, undefined, selectionFileCount);
        },
        uploadFilesAndWaitForRegistration: (files) => handleFileInputAndWaitForRegistration(Array.from(files)),
        clearFiles: handleClearFiles,
    }));
    const handleClearFiles = useCallback(() => {
        setUploadedFiles([]);
        setStatus('idle');
        onComplete?.([]);
        if (fileInputRef.current?.value) {
            fileInputRef.current.value = '';
        }
        setUploadSource(null);
    }, [fileInputRef, onComplete]);
    const handleRemoveFile = useCallback((fileUrl, fileUUID, fileId) => {
        setUploadedFiles((files) => {
            let newFiles = files;
            if (fileUrl) {
                newFiles = files.filter((file) => file.nextURL !== fileUrl);
            }
            else if (fileUUID) {
                newFiles = files.filter((file) => !file.nextURL?.includes(fileUUID));
            }
            else if (fileId) {
                newFiles = files.filter((file) => file.id !== fileId);
            }
            if (newFiles.length === 0) {
                setStatus('idle');
            }
            onComplete?.(newFiles
                .filter((file) => !!file.nextURL)
                .map((file) => ({
                url: file.nextURL ?? '',
                file: file.file,
                resizedFile: file.resizedFile,
                metadata: file.metadata,
            })) ?? []);
            onRemoveFile?.(fileUrl, fileUUID, fileId);
            return newFiles;
        });
        if (fileInputRef.current?.value) {
            fileInputRef.current.value = '';
        }
    }, [fileInputRef, onComplete, onRemoveFile]);
    const removeAllFiles = useCallback(() => {
        setUploadedFiles([]);
        setStatus('idle');
        if (fileInputRef.current?.value) {
            fileInputRef.current.value = '';
        }
        setUploadSource(null);
    }, [fileInputRef]);
    const handleFileError = useCallback((errorMessage) => {
        setWarningMessage(null);
        setErrorMessage(errorMessage ??
            $t(ATTACHMENT_FAILURE_MESSAGES[AttachmentUploadErrorCodes.generic_upload_error]));
        if (uploadedFiles?.length) {
            setStatus('success');
        }
    }, [$t, uploadedFiles?.length]);
    const handleOpenLoginModal = useCallback(() => {
        if (!openLoginModal) {
            return;
        }
        const title = $t({
            defaultMessage: 'Sign in to upload files and photos',
            id: '549e96e1bc',
            description: 'Title in the use File Upload shared utilities section',
        });
        const description = $t({
            defaultMessage: 'Analyze files and photos for free',
            description: 'Description in the useFileUpload shared utilities section',
            id: 'eead8296ab',
        });
        if (openVisitorLoginUpsell) {
            openVisitorLoginUpsell({
                title,
                description,
                origin: ModalOrigin.FILE_UPLOAD,
                sheetModalVariant: isFollowUp ? 'bottom-sheet-gradient' : 'full-sheet',
            });
        }
        else {
            // non-ask-app login modal
            openLoginModal({
                origin: ModalOrigin.FILE_UPLOAD,
                pitchMessage: { title, description },
            });
        }
    }, [$t, isFollowUp, openLoginModal, openVisitorLoginUpsell]);
    const handleOpenPricingModal = useCallback(() => {
        if (!openPricingModal) {
            return;
        }
        const title = $t({
            defaultMessage: 'Upgrade for additional document analysis',
            id: '54749f9273',
            description: 'Title in the use File Upload shared utilities section',
        });
        openPricingModal({
            pitchMessage: {
                title,
            },
            origin: ModalOrigin.FILE_UPLOAD,
        });
    }, [$t, openPricingModal]);
    const gateUpload = useCallback(() => {
        if (gateUploadFile) {
            const gateResult = gateUploadFile();
            return gateResult === true ? 'no_permission' : gateResult || null;
        }
        if (!isSignedIn && !specialCapabilities.fileUpload) {
            handleOpenLoginModal();
            return 'not_signed_in';
        }
        if (uploadRateLimit !== undefined && uploadRateLimit <= 0) {
            handleOpenPricingModal();
            return 'rate_limited';
        }
        return null;
    }, [
        gateUploadFile,
        handleOpenLoginModal,
        handleOpenPricingModal,
        isSignedIn,
        specialCapabilities.fileUpload,
        uploadRateLimit,
    ]);
    const processSubscriptionResult = useCallback((attachmentData, filename) => {
        const file_uuid = attachmentData.file_uuid;
        const newStatus = attachmentData.success ||
            isIgnorableAttachmentError({
                skipParsing,
                errorCode: attachmentData.error_code ?? null,
            })
            ? 'success'
            : 'failed';
        if (newStatus === 'failed') {
            handleFileError($t(ATTACHMENT_FAILURE_MESSAGES[attachmentData.error_code ?? AttachmentProcessingErrorCodes.parsing_error], {
                filename,
            }));
            setUploadedFiles((files) => files.filter((file) => file.file_uuid !== file_uuid));
        }
        else {
            if (attachmentData.token_limit_exceeded) {
                setHasTokenLimitExceeded(true);
                setWarningMessage($t(ATTACHMENT_FAILURE_MESSAGES.token_limit_exceeded, {
                    filename,
                }));
            }
            setUploadedFiles((files) => files.map((obj) => obj.file_uuid === file_uuid
                ? { ...obj, nextURL: attachmentData.s3_url ?? obj.nextURL, status: newStatus }
                : obj));
        }
    }, [handleFileError, $t, skipParsing]);
    const handleSubscription = useCallback(async (file_uuid, filename) => {
        const attachmentData = await subscribeAttachments({
            request: { file_uuids: [file_uuid] },
            reason,
            headers: buildRequestHeaders({}),
        });
        processSubscriptionResult(attachmentData, filename);
    }, [processSubscriptionResult, reason, buildRequestHeaders]);
    const validateFile = useCallback((file) => {
        if (!skipParsing &&
            ((file instanceof File && BLOCKED_UPLOAD_MIME_TYPES.includes(file.type)) ||
                (!(file instanceof File) &&
                    file.mimeType &&
                    BLOCKED_UPLOAD_MIME_TYPES.includes(file.mimeType)))) {
            return $t(ATTACHMENT_FAILURE_MESSAGES[AttachmentUploadErrorCodes.unsupported_type]);
        }
        else if ((file instanceof File && file.size > maxAttachmentFileSizeMB * BYTES_PER_MB) ||
            (!(file instanceof File) &&
                file.sizeBytes &&
                file.sizeBytes > maxAttachmentFileSizeMB * BYTES_PER_MB)) {
            return $t(ATTACHMENT_FAILURE_MESSAGES[AttachmentUploadErrorCodes.too_large], {
                max: readableMaxAttachmentFileSize,
            });
        }
        else if ((file instanceof File &&
            file.size < MIN_FILE_UPLOAD_SIZE &&
            !(import.meta.env.VITE_IS_PERPLEXITY_DESKTOP_BUILD &&
                reason === 'ask-input-inner-followup' &&
                /^\/computer\/tasks\/local-[\w-]+$/.test(window.location.pathname))) ||
            (!(file instanceof File) && file.sizeBytes && file.sizeBytes < MIN_FILE_UPLOAD_SIZE)) {
            return $t(ATTACHMENT_FAILURE_MESSAGES.too_small);
        }
        else if (!file.name) {
            return $t(ATTACHMENT_FAILURE_MESSAGES.no_name);
        }
        return null;
    }, [$t, readableMaxAttachmentFileSize, maxAttachmentFileSizeMB, skipParsing, reason]);
    // 4. Handle fetch response: Process the response from the fetch
    // Returns true if the file needs attachment processing subscription
    const handleFetchResponse = useCallback(async (response, file, url, key, upload_id, attempt, file_uuid, skipSubscription) => {
        const terminalAttempt = { ...attempt, fileUUID: file_uuid ?? null };
        if (!response.ok) {
            handleFileError();
            logger.warn(`[localAttachmentUpload] upload failed for ${file_uuid}, response=${response}`);
            completed(terminalAttempt, 'failure', 'storage_rejected');
            return false;
        }
        let nextURL = null;
        try {
            const data = await response.json();
            if (data?.moderation?.[0]?.status === 'rejected') {
                handleFileError($t(ATTACHMENT_FAILURE_MESSAGES.failed_moderation));
                logger.warn(`[localAttachmentUpload] upload failed moderation for ${file_uuid}, response=${response}`);
                completed(terminalAttempt, 'failure', 'moderation_rejected');
                return false;
            }
            // cleanCloudinaryUrl removes the signature segment
            // from secure_url to keep the file url private
            nextURL = cleanCloudinaryUrl(data?.eager?.[0]?.secure_url ?? data?.secure_url);
        }
        catch {
            // S3 does not return headers due to CORS
            nextURL = `${url.replace(/\/$/, '')}/${key}`.replace('${filename}', file.name);
        }
        if (!nextURL) {
            handleFileError();
            completed(terminalAttempt, 'failure', 'storage_rejected');
            return false;
        }
        const fileStatus = isCloudinaryURL(nextURL) ? 'success' : 'parsing';
        setUploadedFiles((oldFiles) => oldFiles.map((oldFile) => oldFile.id === upload_id
            ? { ...oldFile, nextURL, file_uuid, status: fileStatus }
            : oldFile));
        setStatus('success');
        setUploadSource('local');
        completed(terminalAttempt, 'success');
        if (file_uuid && fileStatus == 'parsing') {
            if (!skipSubscription) {
                await handleSubscription(file_uuid, file.name);
            }
            return true;
        }
        return false;
    }, [completed, handleFileError, $t, handleSubscription]);
    const uploadConnector = useCallback(async (file, source, attempt) => {
        const terminal = await uploadConnectorFile({
            request: {
                remote_file_id: file.id,
                connection_type: source.toUpperCase(),
                reason,
                headers: buildRequestHeaders({}),
                analytics: analyticsSurface
                    ? { uploadAttemptUUID: attempt.uploadAttemptUUID }
                    : undefined,
            },
            onUploaded: async ({ filename, originalUrl, url, fileUUID, status }) => {
                const replacementFile = await createImageFileFromStorage({
                    filename,
                    getImageUrl,
                    mimeType: getMimeTypeFromUrl(url),
                    url: originalUrl,
                }).catch(() => null);
                setUploadedFiles((files) => files.map((uploaded) => uploaded.id === file.id
                    ? {
                        ...uploaded,
                        file: replacementFile ?? uploaded.file,
                        nextURL: url,
                        file_uuid: fileUUID,
                        status,
                        thumbnailSource: replacementFile
                            ? URL.createObjectURL(replacementFile)
                            : undefined,
                    }
                    : uploaded));
                setStatus('success');
                setUploadSource(source);
            },
            onProcessing: async (fileUUID, filename) => {
                try {
                    await handleSubscription(fileUUID, filename);
                }
                catch (error) {
                    logger.warn(`[connectorAttachmentUpload] processing subscription failed for ${fileUUID}, error=${error}`);
                }
            },
        });
        if (terminal.outcome === 'aborted')
            return;
        if (terminal.outcome === 'failure') {
            if (terminal.errorCode === 'rate_limited') {
                handleOpenPricingModal();
                removeFile(file.id);
            }
            else {
                handleFileError();
            }
            completed(attempt, 'failure', terminal.errorCode);
            return;
        }
        completed({ ...attempt, fileUUID: terminal.fileUUID }, 'success');
    }, [
        reason,
        buildRequestHeaders,
        analyticsSurface,
        getImageUrl,
        handleSubscription,
        handleFileError,
        handleOpenPricingModal,
        removeFile,
        completed,
    ]);
    // Returns true if the file needs attachment processing subscription
    const handleCreateUploadUrlResponse = useCallback(async (res, attachmentUpload, attempt, skipSubscription) => {
        const removeErroredFile = () => setUploadedFiles((files) => files.filter((file) => file.id !== attachmentUpload.id));
        const terminalAttempt = { ...attempt, fileUUID: res?.file_uuid ?? null };
        if (res?.error) {
            const explained = SELF_CONTAINED_UPLOAD_ERRORS.find((code) => code === res.error);
            handleFileError(explained ? $t(ATTACHMENT_FAILURE_MESSAGES[explained]) : undefined);
            removeErroredFile();
            completed(terminalAttempt, 'failure', res.error === 'attachments_disabled_by_organization'
                ? 'org_uploads_disabled'
                : 'presign_failed');
            return false;
        }
        if (res?.rate_limited) {
            handleOpenPricingModal();
            removeErroredFile();
            removeFile(attachmentUpload.id);
            completed(terminalAttempt, 'failure', 'rate_limited');
            return false;
        }
        if (!res || !res.fields || !res.s3_bucket_url) {
            handleFileError();
            removeErroredFile();
            completed(terminalAttempt, 'failure', 'presign_failed');
            return false;
        }
        if (isFileImage(attachmentUpload)) {
            setUploadedFiles((currentUploads) => currentUploads.map((currentUpload) => currentUpload.id === attachmentUpload.id
                ? {
                    ...currentUpload,
                    thumbnailSource: URL.createObjectURL(attachmentUpload.file),
                }
                : currentUpload));
        }
        const formData = new FormData();
        Object.entries(res.fields).forEach(([key, value]) => {
            formData.append(key, value);
        });
        formData.append('file', attachmentUpload.file);
        try {
            const response = await fetch(res.s3_bucket_url, {
                method: 'POST',
                body: formData,
            });
            return await handleFetchResponse(response, attachmentUpload.file, res.s3_bucket_url, res.fields.key, attachmentUpload.id, terminalAttempt, res.file_uuid ?? undefined, skipSubscription);
        }
        catch (error) {
            logger.warn(`[localAttachmentUpload] upload failed for ${res.file_uuid} with error=${error}`);
            handleFileError();
            removeErroredFile();
            completed(terminalAttempt, 'failure', 'network_drop');
            return false;
        }
    }, [completed, handleFileError, $t, handleFetchResponse, handleOpenPricingModal, removeFile]);
    // Estimate the total number of tokens for the uploaded files and check if it exceeds the max total tokens
    const isTotalTokensExceeded = useCallback((files) => {
        const estTotalTokens = files
            .filter((file) => file.status === 'success')
            .reduce((acc, file) => {
            const fileTypeSubtype = file.file.type;
            const fileType = fileTypeSubtype.split('/')[0];
            const typeSubtypeBytesPerToken = attachmentTokenEstimationParams?.type_subtype_bytes_per_token?.[fileTypeSubtype];
            const typeBytesPerToken = attachmentTokenEstimationParams?.type_bytes_per_token?.[fileType];
            const defaultBytesPerToken = attachmentTokenEstimationParams?.default_bytes_per_token;
            const bytesPerToken = typeSubtypeBytesPerToken ?? typeBytesPerToken ?? defaultBytesPerToken;
            const fileSize = file.file.size;
            const tokens = bytesPerToken ? fileSize / bytesPerToken : 0;
            return acc + tokens;
        }, 0);
        const maxTotalTokens = attachmentTokenEstimationParams?.max_total_tokens;
        return maxTotalTokens && estTotalTokens > maxTotalTokens;
    }, [attachmentTokenEstimationParams]);
    useEffect(() => {
        // Only check if we have files and they're all in a final state
        if (!uploadedFiles.length) {
            return;
        }
        // don't show multiple warnings if we've already hit a token-limit condition
        if (hasTokenLimitExceeded) {
            return;
        }
        const allFilesComplete = uploadedFiles.every((file) => file.status === 'success' || file.status === 'failed');
        if (allFilesComplete) {
            const exceeded = isTotalTokensExceeded(uploadedFiles);
            if (exceeded) {
                setWarningMessage($t({
                    defaultMessage: 'Performance may be degraded for larger files.',
                    id: '028553534d',
                    description: 'Text in the use File Upload shared utilities section',
                }));
            }
        }
    }, [uploadedFiles, isTotalTokensExceeded, $t, hasTokenLimitExceeded]);
    const uploadFilesBatch = useCallback(async (args) => {
        const { fileList, attempts, fileSource, setUploadRateLimit, metadata, onFilesRegistered, onFilesRegistrationRolledBack, } = args;
        const validFilesWithUuids = [];
        // First pass: validate files and create resize promises
        const resizePromises = fileList.map(async (file) => {
            const errorMessage = validateFile(file);
            if (errorMessage) {
                return { originalFile: file, file, error: errorMessage };
            }
            let fileToUpload = file;
            if ((fileSource ?? defaultFileSource) !== 'asi' && isImage(file) && !isSvg(file)) {
                try {
                    fileToUpload = await resizeImageToMaxDimension(file);
                }
                catch {
                    logger.warn('Unable to resize image file', { fileNameLength: file.name.length });
                }
            }
            return { originalFile: file, file: fileToUpload, error: null };
        });
        // Wait for all resize operations to complete in parallel
        const resizedFiles = await Promise.all(resizePromises);
        // Second pass: process results and create upload entries
        for (const result of resizedFiles) {
            const attempt = attempts.get(result.originalFile);
            if (!attempt)
                continue;
            const uuid = attempt.uploadAttemptUUID;
            if (result.error) {
                handleFileError(result.error);
                completed(attempt, 'failure', result.file.size > maxAttachmentFileSizeMB * BYTES_PER_MB
                    ? 'file_too_large'
                    : 'unsupported_type');
                continue;
            }
            const uploadedFile = {
                id: uuid,
                file: result.file,
                status: 'uploading',
                metadata,
            };
            validFilesWithUuids.push({
                uuid,
                file: result.file,
                uploadedFile,
                attempt,
            });
            setUploadedFiles((files) => [...(files ?? []), uploadedFile]);
        }
        onFilesRegistered?.(validFilesWithUuids.map(({ uuid }) => uuid));
        if (validFilesWithUuids.length === 0)
            return;
        let batchResults;
        try {
            batchResults = await batchCreateUploadUrls({
                filesWithUuids: validFilesWithUuids.map(({ uuid, file }) => ({
                    uuid,
                    file,
                })),
                fileSource: fileSource ?? defaultFileSource,
                forceImage,
                reason,
                skipParsing,
                headers: buildRequestHeaders({}),
            });
        }
        catch (error) {
            validFilesWithUuids.forEach(({ attempt }) => completed(attempt, 'failure', 'presign_failed'));
            throw error;
        }
        const filesToSubscribe = [];
        let sentFilesCount = 0;
        if (batchResults) {
            await Promise.all(validFilesWithUuids.map(async ({ uuid, uploadedFile, attempt }) => {
                const res = batchResults[uuid];
                if (!res) {
                    removeFile(uploadedFile.id);
                    completed(attempt, 'failure', 'presign_failed');
                    return;
                }
                const needsSubscription = await handleCreateUploadUrlResponse(res, uploadedFile, attempt, true);
                if (needsSubscription && res.file_uuid) {
                    filesToSubscribe.push({
                        file_uuid: res.file_uuid,
                        filename: uploadedFile.file.name,
                    });
                }
                // A refused entry never left the device, so it must not spend the
                // user's upload quota.
                if (!res.error && !res.rate_limited) {
                    sentFilesCount++;
                }
            }));
            if (filesToSubscribe.length > 0) {
                const fileUuidToFilename = new Map(filesToSubscribe.map(({ file_uuid, filename }) => [file_uuid, filename]));
                try {
                    await subscribeAttachmentsBatch({
                        request: {
                            file_uuids: filesToSubscribe.map(({ file_uuid }) => file_uuid),
                        },
                        reason,
                        headers: buildRequestHeaders({}),
                        onMessage: (data) => {
                            processSubscriptionResult(data, fileUuidToFilename.get(data.file_uuid) ?? 'unknown');
                        },
                    });
                }
                catch (error) {
                    logger.warn(`[localAttachmentUpload] batch subscription failed with error=${error}`);
                    for (const { file_uuid } of filesToSubscribe) {
                        setUploadedFiles((files) => files.map((f) => f.file_uuid === file_uuid && f.status === 'parsing'
                            ? { ...f, status: 'failed' }
                            : f));
                    }
                    handleFileError();
                }
            }
        }
        else {
            onFilesRegistrationRolledBack?.(validFilesWithUuids.map(({ uuid }) => uuid));
            // remove failed files
            validFilesWithUuids.forEach(({ uploadedFile, attempt }) => {
                removeFile(uploadedFile.id);
                completed(attempt, 'failure', 'presign_failed');
            });
            handleFileError($t({
                defaultMessage: "We couldn't upload your file. Please try again.",
                id: '8e143bf88d',
                description: 'Error message in the use File Upload shared utilities section',
            }));
        }
        if (uploadRateLimit && uploadRateLimit > 0 && setUploadRateLimit) {
            setUploadRateLimit(sentFilesCount);
        }
    }, [
        validateFile,
        handleFileError,
        forceImage,
        reason,
        skipParsing,
        defaultFileSource,
        handleCreateUploadUrlResponse,
        processSubscriptionResult,
        uploadRateLimit,
        removeFile,
        $t,
        buildRequestHeaders,
        completed,
        maxAttachmentFileSizeMB,
    ]);
    // 2. Validate and upload files: Validate each file, handle errors if necessary, and if no errors proceed with upload
    const validateAndUploadFiles = useCallback((fileList, attempts, source, fileSource, metadata, onFilesRegistered, onFilesRegistrationRolledBack) => {
        if (isListOfFiles(fileList)) {
            void uploadFilesBatch({
                fileList,
                attempts,
                fileSource,
                setUploadRateLimit,
                metadata,
                onFilesRegistered,
                onFilesRegistrationRolledBack,
            }).catch(() => onFilesRegistered?.([]));
        }
        else if (source) {
            // file upload from connector
            const registeredFileIds = [];
            fileList.forEach((file) => {
                const attempt = attempts.get(file);
                if (!attempt)
                    return;
                const errorMessage = validateFile(file);
                if (errorMessage) {
                    handleFileError(errorMessage);
                    completed(attempt, 'failure', file.sizeBytes && file.sizeBytes > maxAttachmentFileSizeMB * BYTES_PER_MB
                        ? 'file_too_large'
                        : 'unsupported_type');
                }
                else {
                    const mimeType = getMimeTypeFromUrl(file.name ?? '');
                    const dummyFile = new File([''], file.name ?? '', {
                        type: mimeType,
                    });
                    const uploadedFile = {
                        id: file.id,
                        file: dummyFile,
                        status: 'uploading',
                        metadata,
                    };
                    setUploadedFiles((files) => [...(files ?? []), uploadedFile]);
                    registeredFileIds.push(uploadedFile.id);
                    void uploadConnector(file, source, attempt);
                }
            });
            onFilesRegistered?.(registeredFileIds);
        }
        else {
            onFilesRegistered?.([]);
        }
    }, [
        validateFile,
        handleFileError,
        uploadConnector,
        uploadFilesBatch,
        setUploadRateLimit,
        completed,
        maxAttachmentFileSizeMB,
    ]);
    // 1. Initial checks: Ensure the user is signed in, the upload rate limit is not exceeded, and the number of files uploaded is valid
    const handleFileInput = useCallback((files, source, fileSource, metadata, selectionFileCount, onFilesRegistered, onFilesRegistrationRolledBack) => {
        if (!files || files.length === 0) {
            onFilesRegistered?.([]);
            return;
        }
        const resolvedSource = source ?? 'local';
        const resolvedSelectionFileCount = selectionFileCount ?? files.length;
        const attempts = startAttempts(files, resolvedSource, resolvedSelectionFileCount);
        // Short circuit for entropy source, which is used for screenshot uploads from the sidecar
        // We currently allow effectively unlimited screenshot uploads from the sidecar
        if (fileSource !== ENTROPY_SOURCE) {
            // Pre-counted selections may be capped before they reach this hook. Preserve the
            // selection-level rejection reason before applying per-request auth and quota gates.
            if (selectionFileCount !== undefined &&
                resolvedSelectionFileCount + (uploadedFiles?.length ?? 0) > maxFilesUpload) {
                rejectTooManyFiles(attempts);
                onFilesRegistered?.([]);
                return;
            }
            const gateError = gateUpload();
            if (gateError) {
                attempts.forEach((attempt) => completed(attempt, 'failure', gateError));
                onFilesRegistered?.([]);
                return;
            }
            if (resolvedSelectionFileCount + (uploadedFiles?.length ?? 0) > maxFilesUpload) {
                rejectTooManyFiles(attempts);
                onFilesRegistered?.([]);
                return;
            }
        }
        onStart?.();
        setStatus('loading');
        validateAndUploadFiles(files, attempts, source, fileSource, metadata, onFilesRegistered, onFilesRegistrationRolledBack);
    }, [
        uploadedFiles,
        maxFilesUpload,
        validateAndUploadFiles,
        onStart,
        gateUpload,
        completed,
        rejectTooManyFiles,
        startAttempts,
    ]);
    const handleFileInputAndWaitForRegistration = useCallback((files, source, fileSource, metadata) => new Promise((resolve) => {
        let resolveRollback;
        const rolledBackFileIds = new Promise((rollbackResolve) => {
            resolveRollback = rollbackResolve;
        });
        handleFileInput(files, source, fileSource, metadata, undefined, (registeredFileIds) => resolve({ registeredFileIds, rolledBackFileIds }), resolveRollback);
    }), [handleFileInput]);
    // inject a pre-existing URL directly as an attachment without fetching or re-uploading
    const addAttachmentUrl = useCallback((url, filename, metadata) => {
        const id = safeRandomUUID();
        const file = new File([], filename);
        setUploadedFiles((prev) => [
            ...prev,
            { id, file, nextURL: url, status: 'success', metadata },
        ]);
        setStatus('success');
    }, []);
    const userSettingsQuery = useQuery({
        queryKey: getUserSettingsQueryKey({
            skipConnectorPickerCredentials: false,
        }),
        queryFn: () => {
            const headers = buildRequestHeaders(locale ? { 'accept-language': locale } : {});
            return getUserSettingsFunc({
                reason,
                headers,
                skipConnectorPickerCredentials: false,
            });
        },
        enabled: false,
    });
    const getConnectorWithCredentials = useCallback(async (connectorId) => {
        const getSettings = async () => {
            if (userSettingsQuery.status === 'success') {
                return userSettingsQuery.data;
            }
            const { data } = await userSettingsQuery.refetch();
            return data;
        };
        const settings = await getSettings();
        const connector = settings?.connectors.connectors.find((connector) => connector.name === connectorId);
        if (!connector) {
            logger.error(`Connector ${connectorId} not found in user settings`);
            return null;
        }
        return connector;
    }, [userSettingsQuery]);
    const { openPicker: openGoogleDrivePickerInternal } = useGoogleDrivePicker({
        isAudioVideoFilesEnabled: isAudioVideoFilesEnabled,
    });
    const openGoogleDrivePicker = useCallback(async (options) => {
        const connector = await getConnectorWithCredentials('google_drive');
        if (!connector) {
            openToast({
                variant: 'error',
                message: $t({
                    defaultMessage: 'Failed to open Google Drive file picker',
                    id: '84fc76e136',
                    description: 'Toast notification in the use File Upload shared utilities section',
                }),
                timeoutSeconds: 5,
                dedupKey: 'file-picker-error',
            });
            return;
        }
        openGoogleDrivePickerInternal({
            ...options,
            apiKey: connector.picker_credentials?.api_key ?? '',
            clientId: connector.picker_credentials?.client_id ?? '',
            loginHint: connector.connection_display_name ?? '',
            promptBehavior: options.promptBehavior ?? googlePromptBehavior,
            scopes: connector.picker_credentials?.scopes,
        });
    }, [
        getConnectorWithCredentials,
        openGoogleDrivePickerInternal,
        googlePromptBehavior,
        openToast,
        $t,
    ]);
    const [isBoxOpen, setIsBoxOpen] = useState(false);
    const onCloseBox = useCallback(() => setIsBoxOpen(false), []);
    const handleBoxFiles = useCallback((items) => {
        const pickerItems = items.map((item) => boxItemToPickerDocument(item));
        handleFileInput(pickerItems, 'box');
        onCloseBox();
    }, [handleFileInput, onCloseBox]);
    const { openPicker: openBoxPickerInternal, cleanupPicker: cleanupBoxPicker } = useBoxPicker({
        mode: 'attachment',
        isAudioVideoFilesEnabled: isAudioVideoFilesEnabled,
        onPicked: handleBoxFiles,
        onCancel: onCloseBox,
    });
    const openBoxPicker = useCallback(async (options = {}) => {
        const connector = await getConnectorWithCredentials('box');
        if (!connector) {
            openToast({
                variant: 'error',
                message: $t({
                    defaultMessage: 'Failed to open Box file picker',
                    id: 'ac65642b0c',
                    description: 'Toast notification in the use File Upload shared utilities section',
                }),
                timeoutSeconds: 5,
                dedupKey: 'file-picker-error',
            });
            return;
        }
        const expiresAtString = connector.picker_credentials?.expires_at;
        if (expiresAtString && refreshBoxCredentials) {
            const expiresAt = new Date(expiresAtString);
            if (isNaN(expiresAt.getTime())) {
                setIsBoxOpen(true);
                return;
            }
            // The user settings query provides credentials that are valid for one
            // hour. If for whatever reason, that isn't enough time for the user,
            // this request will refresh the credentials for them.
            const now = new Date();
            if (expiresAt < now) {
                const credentials = await refreshBoxCredentials(connector.connection_uuid);
                openBoxPickerInternal({
                    ...options,
                    apiKey: credentials?.api_key ?? '',
                });
                return;
            }
        }
        openBoxPickerInternal({
            ...options,
            apiKey: connector.picker_credentials?.api_key ?? '',
        });
    }, [getConnectorWithCredentials, openBoxPickerInternal, refreshBoxCredentials, openToast, $t]);
    const onOpenBox = useCallback(async () => {
        setIsBoxOpen(true);
    }, []);
    // Box uses a CSS selector to inject an iframe into a
    // div that renders the file picker. The file picker div
    // lives within a modal and is only mounted once the modal
    // is open. The picker waits for the modal to finish opening
    // before it opens itself.
    useEffect(() => {
        if (isBoxOpen) {
            openBoxPicker();
        }
    }, [isBoxOpen, openBoxPicker]);
    const onMicrosoftFilesPicked = useCallback((items, source) => {
        if (!toPickerDocument || (source !== 'onedrive' && source !== 'sharepoint')) {
            return;
        }
        const pickerDocs = items
            .map((item) => toPickerDocument(item, source))
            .filter((item) => item !== null);
        handleFileInput(pickerDocs, source);
        closeMicrosoftPicker?.();
    }, [toPickerDocument, handleFileInput, closeMicrosoftPicker]);
    const { openPicker: openDropboxPickerInternal } = useDropboxPicker();
    const openDropboxPicker = useCallback(async (options) => {
        const connector = await getConnectorWithCredentials('dropbox');
        if (!connector) {
            openToast({
                variant: 'error',
                message: $t({
                    defaultMessage: 'Failed to open Dropbox file picker',
                    id: 'af29658f1d',
                    description: 'Toast notification in the use File Upload shared utilities section',
                }),
                timeoutSeconds: 5,
                dedupKey: 'file-picker-error',
            });
            return;
        }
        openDropboxPickerInternal({
            ...options,
            clientId: connector.picker_credentials?.client_id ?? '',
        });
    }, [getConnectorWithCredentials, openDropboxPickerInternal, openToast, $t]);
    const onDropboxFilesPicked = useCallback((items) => {
        const pickerItems = items
            .map((item) => dropboxItemToPickerDocument(item))
            .filter((item) => item !== null);
        handleFileInput(pickerItems, 'dropbox');
    }, [handleFileInput]);
    const handlePickerError = useCallback(() => handleFileError($t({
        defaultMessage: 'Unable to retrieve files from connector.',
        id: '978d846f2b',
        description: 'Error message in the use File Upload shared utilities section',
    })), [handleFileError, $t]);
    const openMicrosoftPicker = useCallback(async (connectorId, options) => {
        if (!openMicrosoftPickerInternal) {
            return;
        }
        const connector = await getConnectorWithCredentials(connectorId);
        if (!connector) {
            const message = connectorId === 'onedrive'
                ? $t({
                    defaultMessage: 'Failed to open OneDrive file picker',
                    id: '025e821eb3',
                    description: 'Error message in the use File Upload shared utilities section',
                })
                : $t({
                    defaultMessage: 'Failed to open SharePoint file picker',
                    id: '418d3e2678',
                    description: 'Error message in the use File Upload shared utilities section',
                });
            openToast({
                variant: 'error',
                message,
                timeoutSeconds: 5,
                dedupKey: 'file-picker-error',
            });
            return;
        }
        openMicrosoftPickerInternal({
            ...options,
            connector: connectorId,
            clientId: connector.picker_credentials?.client_id ?? '',
            tenantName: connector.picker_credentials?.tenant_name ?? '',
            loginHint: connector.connection_display_name ?? '',
            accountIdentifier: connector.picker_credentials?.account_identifier ?? undefined,
            connectionUuid: connector.connection_uuid,
        });
    }, [getConnectorWithCredentials, openMicrosoftPickerInternal, openToast, $t]);
    const handleAttachFromConnector = useCallback((source, webUrl) => {
        if (source == 'google_drive') {
            openGoogleDrivePicker({
                mode: 'attachment',
                onPicked: (files) => handleFileInput(files, source),
            });
        }
        else if (source === 'onedrive' || source === 'sharepoint') {
            openMicrosoftPicker(source, {
                webUrl: webUrl,
                mode: 'attachment',
                isAudioVideoFilesEnabled: isAudioVideoFilesEnabled,
                onPicked: (items) => onMicrosoftFilesPicked(items, source),
                onError: errorMicrosoftPicker,
                onCancel: closeMicrosoftPicker,
            });
        }
        else if (source === 'dropbox') {
            openDropboxPicker({
                mode: 'attachment',
                isAudioVideoFilesEnabled: isAudioVideoFilesEnabled,
                onPicked: onDropboxFilesPicked,
                onError: handlePickerError,
            });
        }
        else if (source == 'box') {
            onOpenBox();
        }
    }, [
        openGoogleDrivePicker,
        handleFileInput,
        openMicrosoftPicker,
        onMicrosoftFilesPicked,
        closeMicrosoftPicker,
        errorMicrosoftPicker,
        openDropboxPicker,
        onDropboxFilesPicked,
        handlePickerError,
        onOpenBox,
        isAudioVideoFilesEnabled,
    ]);
    const handleStartUpload = useCallback((source) => {
        const gateError = gateUpload();
        if (gateError) {
            const attemptId = safeRandomUUID();
            completed(startAttempt(attemptId, null, source ?? 'local', 1), 'failure', gateError);
            return;
        }
        if (!source || source === 'local') {
            fileInputRef.current?.click();
            onFilePickerOpen?.();
        }
        else {
            handleAttachFromConnector(source);
        }
    }, [
        fileInputRef,
        gateUpload,
        handleAttachFromConnector,
        onFilePickerOpen,
        completed,
        startAttempt,
    ]);
    useEffect(() => {
        let timer = null;
        if (warningMessage) {
            timer = setTimeout(() => {
                setWarningMessage(null);
            }, ERROR_MESSAGE_TIMEOUT);
        }
        return () => {
            if (timer) {
                clearTimeout(timer);
            }
        };
    }, [warningMessage]);
    useEffect(() => {
        if (uploadedFiles) {
            onComplete?.(uploadedFiles
                .filter((file) => !!file.nextURL)
                .map((uploadedFile) => ({
                url: uploadedFile.nextURL ?? '',
                file: uploadedFile.file,
                resizedFile: uploadedFile.resizedFile,
                metadata: uploadedFile.metadata,
            })));
        }
    }, [uploadedFiles, onComplete]);
    return useMemo(() => ({
        errorMessage,
        warningMessage,
        uploadedFiles,
        uploadSource,
        status,
        fileInputRef,
        handleClearFiles,
        handleRemoveFile,
        handleStartUpload,
        handleFileInput,
        handleFileInputAndWaitForRegistration,
        addAttachmentUrl,
        removeAllFiles,
        handleOpenLoginModal,
        handleOpenPricingModal,
        isBoxOpen,
        onCloseBox,
        cleanupBoxPicker,
        setErrorMessage,
    }), [
        errorMessage,
        warningMessage,
        uploadedFiles,
        uploadSource,
        status,
        fileInputRef,
        handleClearFiles,
        handleRemoveFile,
        handleStartUpload,
        handleFileInput,
        handleFileInputAndWaitForRegistration,
        addAttachmentUrl,
        removeAllFiles,
        handleOpenLoginModal,
        handleOpenPricingModal,
        isBoxOpen,
        onCloseBox,
        cleanupBoxPicker,
    ]);
};
//# sourceMappingURL=useFileUpload.js.map